import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";

/**
 * Canvassers Finance Tab — program P&L (Ship 7, May 22, 2026).
 * Spec: memory/features/canvasser-program.md
 *
 * Answers "are we making or losing money on the canvasser program / each canvasser?"
 *   Cost      = total weekly pay (hourly + per-app), from canvasser_payouts.
 *   Captured  = the 5% RM carve-out on their built deals (realized) + projected
 *               from in-flight apps, from canvasser_captured_revenue / canvasser_pnl.
 *   Breakeven = net (realized + projected) ≥ 0.
 *
 * Honest about timing: pay is spent now; captured revenue lands months later
 * (claim → build → close), so realized net runs negative early. The projected
 * column closes that gap with in-flight apps.
 */

interface PnlRow {
  canvasser_id: string;
  full_name: string;
  is_active: boolean;
  hours_to_date: number | string;
  total_paid: number | string;
  apps_built: number;
  apps_inflight: number;
  apps_dead: number;
  realized_captured: number | string;
  projected_captured: number | string;
  net_realized: number | string;
  net_projected: number | string;
}

function n(v: number | string | null | undefined): number {
  return typeof v === "number" ? v : Number(v) || 0;
}

export default function CanvassersFinanceTab() {
  const [rows, setRows] = useState<PnlRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc("canvasser_pnl");
    if (error) { setErr(formatSupabaseError(error)); return; }
    setErr(null);
    setRows((data ?? []) as PnlRow[]);
  }, []);

  useEffect(() => { load(); }, [load]);
  useRealtimeTable("canvasser_payouts", load);
  useRealtimeTable("canvasser_captured_revenue", load);

  const totals = useMemo(() => {
    const base = { paid: 0, realized: 0, projected: 0, net_realized: 0, net_projected: 0 };
    if (!rows) return base;
    for (const r of rows) {
      base.paid += n(r.total_paid);
      base.realized += n(r.realized_captured);
      base.projected += n(r.projected_captured);
      base.net_realized += n(r.net_realized);
      base.net_projected += n(r.net_projected);
    }
    return base;
  }, [rows]);

  const sorted = useMemo(
    () => (rows ? [...rows].sort((a, b) => n(b.net_projected) - n(a.net_projected)) : []),
    [rows],
  );

  if (rows === null) {
    return <div className="bg-white rounded-xl shadow p-4 text-sm text-slate-400">Loading canvasser P&amp;L…</div>;
  }

  const status =
    totals.net_realized >= 0
      ? { label: "Profitable", cls: "bg-emerald-100 text-emerald-700" }
      : totals.net_projected >= 0
      ? { label: "On track to break even", cls: "bg-amber-100 text-amber-700" }
      : { label: "Running at a loss", cls: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-5">
      {err && <div className="text-xs bg-red-50 text-red-700 rounded px-3 py-2">{err}</div>}

      {/* Program header */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-bardin-ink">🚪 Canvasser Program P&amp;L</h2>
          <span className={"text-xs font-bold px-2.5 py-1 rounded-full " + status.cls}>{status.label}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Tile label="Pay to date" value={totals.paid} tone="cost" hint="hourly + per-app" />
          <Tile label="Captured (realized)" value={totals.realized} tone="rev" hint="5% on built deals" />
          <Tile label="Captured (projected)" value={totals.projected} tone="rev-soft" hint="in-flight apps" />
          <Tile label="Net (realized)" value={totals.net_realized} tone="net" hint="realized − pay" />
          <Tile label="Net (realized + projected)" value={totals.net_projected} tone="net" hint="breakeven target ≥ $0" />
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Pay is spent now; captured revenue is realized months later when an app builds. Realized net runs
          negative early — the projected column closes the gap from in-flight apps. Goal: net (realized + projected) ≥ $0.
        </p>
      </div>

      {/* Per-canvasser table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-bardin-ink">Per canvasser</div>
        {sorted.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">No canvassers yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="px-3 py-2 font-semibold">Canvasser</th>
                  <th className="px-3 py-2 font-semibold text-right">Hrs</th>
                  <th className="px-3 py-2 font-semibold text-right">Paid</th>
                  <th className="px-3 py-2 font-semibold text-right">Apps (built/flight/dead)</th>
                  <th className="px-3 py-2 font-semibold text-right">Realized</th>
                  <th className="px-3 py-2 font-semibold text-right">Projected</th>
                  <th className="px-3 py-2 font-semibold text-right">Net (real)</th>
                  <th className="px-3 py-2 font-semibold text-right">Net (proj)</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.canvasser_id} className="border-b border-slate-50 last:border-0">
                    <td className="px-3 py-2">
                      <span className="font-medium text-slate-800">{r.full_name}</span>
                      {!r.is_active && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{n(r.hours_to_date).toFixed(0)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(n(r.total_paid))}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                      {r.apps_built}/{r.apps_inflight}/{r.apps_dead}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtUSD(n(r.realized_captured))}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtUSD(n(r.projected_captured))}</td>
                    <td className={"px-3 py-2 text-right tabular-nums font-semibold " + (n(r.net_realized) < 0 ? "text-red-600" : "text-emerald-700")}>
                      {fmtUSD(n(r.net_realized))}
                    </td>
                    <td className={"px-3 py-2 text-right tabular-nums font-semibold " + (n(r.net_projected) < 0 ? "text-red-600" : "text-emerald-700")}>
                      {fmtUSD(n(r.net_projected))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, tone, hint }: { label: string; value: number; tone: "cost" | "rev" | "rev-soft" | "net"; hint?: string }) {
  const color =
    tone === "cost" ? "text-slate-900"
    : tone === "rev" ? "text-emerald-700"
    : tone === "rev-soft" ? "text-emerald-500"
    : value < 0 ? "text-red-600" : "text-emerald-700";
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={"text-base font-bold " + color}>{fmtUSD(value)}</div>
      {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
    </div>
  );
}
